import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useEffect } from 'react';
import { typedFetch } from '../../typedFetch';
import './User.scss';
// https://stackoverflow.com/a/60132060
// Make sure that the carriers listed here are exactly the same as the types
const arrayOfAll = () => (array) => array;
const arrayOfAllPhoneCarriers = arrayOfAll();
const phoneCarriers = arrayOfAllPhoneCarriers([
    '',
    'T-Mobile',
    'AT&T',
    'Verizon',
    'Sprint PCS',
    'Virgin Mobile',
    'Google Fi',
]);
const userMatch = window.location.pathname.match(/^\/users\/([^/]+)$/);
const userSlug = userMatch ? userMatch[1] : '';
const initialize = (new URLSearchParams(window.location.search)).get('initialize') === '1';
const baseUser = {
    guid: 'new',
    name: '',
    email: '',
    phoneNumber: '',
    phoneCarrier: '',
    sendTextOnNewTicket: true,
    enabled: true,
    departments: [],
    supervisor: false,
};
export const User = ({ appendToErrorLog, loggedInUser, }) => {
    const [loading, setLoading] = useState(true);
    const [user, setUser] = useState();
    const [userChanged, setUserChanged] = useState(false);
    useEffect(() => {
        setUserChanged(true);
    }, [user]);
    const [editable, setEditable] = useState(false);
    const [departments, setDepartments] = useState([]);
    useEffect(() => {
        typedFetch('departments', {
            body: {
                action: 'get',
            },
        })
            .then((result) => {
            if (result.success) {
                setDepartments(result.departments);
            }
            else {
                appendToErrorLog(`Attempting to retrieve departments failed: ${result.reason}`);
            }
        })
            .catch((error) => {
            appendToErrorLog(`Unexpected error while retrieving departments: ${String(error['message'])}`);
        });
        if (userSlug === 'new') {
            setUser(baseUser);
            setLoading(false);
            setEditable(Boolean(loggedInUser?.supervisor));
            return;
        }
        typedFetch('get-user', {
            body: {
                userSlug,
            },
        })
            .then((result) => {
            if (result.success) {
                setUser(result.user);
                setEditable(result.editable);
                if (initialize && !result.user.phoneNumber) {
                    appendToErrorLog('Root user successfully created! Please add your phone information.');
                }
                // Wait until the effect that sets userChanged to true finishes running
                setTimeout(() => {
                    setUserChanged(false);
                }, 30);
            }
            else {
                appendToErrorLog(`Login failed: ${result.reason}`);
            }
        })
            .catch((error) => {
            appendToErrorLog(`Unexpected error while verifying PW: ${String(error['message'])}`);
        })
            .finally(() => {
            setLoading(false);
        });
    }, [appendToErrorLog, loggedInUser?.supervisor]);
    const updateUser = (disable) => {
        if (loading) {
            return;
        }
        if (!user) {
            appendToErrorLog("Can't update a nonexistent user");
            return;
        }
        const plainNumber = user.phoneNumber.replaceAll(/\D+/g, '');
        if (plainNumber.length !== 10) {
            appendToErrorLog(`Failed to validate phone number: 10 digits needed, but found: "${plainNumber}"`);
            return;
        }
        if (user.phoneCarrier === '') {
            appendToErrorLog('A phone carrier is required');
            return;
        }
        setLoading(true);
        typedFetch('update-user', { body: { userToUpdate: user, disable } })
            .then((result) => {
            if (result.success) {
                // Reload the page regardless; a new user will have a new /users/?? link, as will someone with a changed email
                window.location.href = `/users/${user.email.match(/[^@]+/)[0]}`;
            }
            else {
                appendToErrorLog(`Something went wrong while attempting to update the user: ${result.reason}`);
            }
        })
            .catch((error) => {
            appendToErrorLog(`An unexpected error occurred while attempting to update the user: ${String(error['message'])}`);
        })
            .finally(() => {
            setLoading(false);
        });
    };
    const testText = () => {
        if (loading) {
            return;
        }
        setLoading(true);
        typedFetch('test-text', {
            body: {
                phoneNumber: user?.phoneNumber,
                phoneCarrier: user?.phoneCarrier,
            },
        })
            .then((result) => {
            if (result.success) {
                appendToErrorLog('The server has attempted to send a text. Please wait a few seconds. If nothing comes through, the phone carrier may have changed their server address.');
            }
            else {
                appendToErrorLog(`Something went wrong while attempting to send a text: ${result.reason}`);
            }
        })
            .catch((error) => {
            appendToErrorLog(`An unexpected error occurred while attempting to send a text: ${String(error['message'])}`);
        })
            .finally(() => {
            setTimeout(() => {
                setLoading(false);
            }, 10_000);
        });
    };
    const revokeTokens = () => {
        const guid = user?.guid;
        if (loading || !guid) {
            return;
        }
        setLoading(true);
        typedFetch('revoke-tokens', {
            body: {
                guid,
            },
        })
            .then((result) => {
            if (result.success) {
                localStorage.removeItem('token');
                window.location.href = '/login';
            }
            else {
                appendToErrorLog(`Something went wrong while attempting to revoke tokens: ${result.reason}`);
            }
        })
            .catch((error) => {
            appendToErrorLog(`An unexpected error occurred while attempting to revoke tokens: ${String(error['message'])}`);
        })
            .finally(() => {
            setLoading(false);
        });
    };
    useEffect(() => {
        if (!loggedInUser?.supervisor && userSlug === 'new') {
            appendToErrorLog('Only supervisors can add new users');
        }
    }, [loggedInUser?.supervisor, appendToErrorLog]);
    if (!userSlug) {
        // Should not happen, since userMatch must match in Core for this to render
        return 'An unexpected error happened while extracting the user from the URL';
    }
    if (loading && !user) {
        return 'Loading...';
    }
    if (!user || !loggedInUser) {
        return 'Error';
    }
    if (!loggedInUser.supervisor && userSlug === 'new') {
        return 'Error';
    }
    const possibleReadonly = editable ? {} : { readOnly: true };
    return (_jsxs("form", { className: `user${loading ? ' loading' : ''}`, onSubmit: (e) => {
            e.preventDefault();
            updateUser();
        }, children: [_jsx("h2", { children: userSlug === 'new' ? 'Add new user' : user.name + (user.enabled ? '' : ' (Disabled)') }), userSlug === 'new' && (_jsxs("div", { className: "labeled-input", children: [_jsx("label", { children: "Name" }), _jsx("input", { required: true, ...possibleReadonly, value: user.name, onChange: (e) => { setUser({ ...user, name: e.currentTarget.value }); } })] })), _jsx("div", { className: "spacer" }), _jsxs("div", { className: "labeled-input", children: [_jsxs("label", { children: ["Email", userSlug === 'new' ? ' (Google)' : ''] }), _jsx("input", { required: true, ...possibleReadonly, type: "email", value: user.email, onChange: (e) => { setUser({ ...user, email: e.currentTarget.value }); } })] }), _jsx("div", { children: `${userSlug === 'new' ? 'A' : 'If an email address is changed, a'} notification email will be sent to the new address.` }), _jsx("div", { className: "spacer" }), _jsxs("div", { className: "labeled-input", children: [_jsx("label", { children: "Phone Number" }), _jsx("input", { required: true, ...possibleReadonly, value: user.phoneNumber, onChange: (e) => { setUser({ ...user, phoneNumber: e.currentTarget.value }); }, placeholder: "512-123-4567" })] }), _jsxs("div", { className: "labeled-input", children: [_jsx("label", { children: "Phone Provider" }), _jsx("select", { required: true, ...possibleReadonly, value: user.phoneCarrier, onChange: (e) => { setUser({ ...user, phoneCarrier: e.currentTarget.value }); }, children: phoneCarriers.map(carrier => _jsx("option", { value: carrier, children: carrier }, carrier)) })] }), _jsxs("label", { className: `labeled-input${!loggedInUser.supervisor && !(user.guid === loggedInUser.guid) ? ' readonly' : ''}`, title: "Will only send a text if the ticket is for your department", children: [_jsx("div", { children: "Send text on new ticket" }), _jsx("input", { type: "checkbox", checked: user.sendTextOnNewTicket, onChange: (e) => {
                            if (loggedInUser.supervisor || user.guid === loggedInUser.guid) {
                                setUser({ ...user, sendTextOnNewTicket: e.currentTarget.checked });
                            }
                        } })] }), _jsx("button", { onClick: testText, children: "Test it!" }), _jsx("div", { className: "spacer" }), _jsxs("label", { className: `labeled-input${loggedInUser.supervisor ? '' : ' readonly'}`, children: [_jsx("div", { children: "Supervisor" }), _jsx("input", { type: "checkbox", checked: user.supervisor, onChange: (e) => {
                            if (loggedInUser.supervisor) {
                                setUser({ ...user, supervisor: e.currentTarget.checked });
                            }
                        } })] }), _jsx("div", { className: "spacer" }), _jsx("p", { children: "Does work for the following department(s):" }), departments.map(department => (_jsxs("label", { className: `labeled-input${loggedInUser.supervisor ? '' : ' readonly'}`, children: [_jsx("div", { children: department }), _jsx("input", { ...possibleReadonly, type: "checkbox", checked: user.departments.includes(department), onChange: (e) => {
                            if (loggedInUser.supervisor) {
                                setUser({ ...user, departments: e.currentTarget.checked ? [...new Set([...user.departments, department])] : user.departments.filter(dept => dept !== department) });
                            }
                        } })] }, department))), departments.length === 0 && !loading && 'No departments exist', _jsx("div", { className: "spacer" }), editable && userSlug !== 'new' ? (_jsxs(_Fragment, { children: [_jsx("div", { children: "Security" }), _jsxs("button", { onClick: revokeTokens, children: ["Log ", userSlug, " out of Tickets on all devices"] }), _jsx("div", { className: "spacer" })] })) : null, _jsx("button", { type: "submit", disabled: !userChanged, children: userSlug === 'new' ? 'Finish creating new user' : 'Save Changes' }), loggedInUser.supervisor && userSlug !== 'new' ? (_jsxs(_Fragment, { children: [_jsx("div", { className: "spacer" }), _jsx("div", { className: "spacer" }), _jsx("div", { className: "spacer" }), _jsxs("button", { className: "disable", onClick: (e) => {
                            e.preventDefault();
                            updateUser(user.enabled ? 'disable' : 'enable');
                        }, children: [user.enabled ? 'Disable ' : 'Enable ', " user"] })] })) : null] }));
};
